import { useEffect, useRef, useState } from 'react';
import ShelfQrScanner from './ShelfQrScanner';
import { pickupStart, pickupScan, pickupCancel, notiz, vintedCode, vintedScanCode, vt } from '../services/vinted';
import { normCode } from '../services/vintedNames';

/**
 * 🧪 Vinted-Ausgabe (Experiment): Vinted gibt ein Paket nur gegen den Abholcode des Kunden heraus.
 * 1. Abholcode scannen oder PIN tippen → Vinted nennt die Pakete dieses Abholers
 * 2. „Übergeben": jedes Paket wird in der Vinted-App bestätigt und danach in PaketPilot ausgegeben
 * Rückfall: antwortet Vinted nicht, kann das angetippte Paket trotzdem ausgegeben werden — mit dem Hinweis,
 * es in der Vinted-App nachzubuchen. Lehnt Vinted den Code ab, wird NICHT ausgegeben (falscher Abholer).
 */
export default function VintedAusgabe({ paket, pakete, colors, language, onPaketAusgeben, onClose }) {
  const T = vt(language);
  const dark = colors?.darkMode;
  const f = { text: dark ? '#fff' : '#23272f', sub: dark ? '#aaa' : '#666', rand: dark ? '#444' : '#ddd', feld: dark ? '#1b1f27' : '#fff', zeile: dark ? '#2d333b' : '#f5f6f8' };

  const [phase, setPhase] = useState('code'); // code | sucht | liste | laeuft | fertig | rueckfall
  const [pin, setPin] = useState('');
  const [fehler, setFehler] = useState('');
  const [vinted, setVinted] = useState([]); // Pakete laut Vinted: {id, shown, name, zone}
  const [ergebnis, setErgebnis] = useState({}); // id → 'ok' | 'fehler'
  // Die Ausgabe in PaketPilot hängt am aktuellen Bestand → immer die neueste Funktion aus dem Parent nehmen
  const ausgebenRef = useRef(onPaketAusgeben); ausgebenRef.current = onPaketAusgeben;
  const paketeRef = useRef(pakete); paketeRef.current = pakete;
  const offen = useRef(false); // läuft in der Vinted-App gerade eine Ausgabe?
  const lebt = useRef(true);
  useEffect(() => () => { lebt.current = false; if (offen.current) pickupCancel(); }, []);

  /**
   * Offenes PaketPilot-Paket zu einem Eintrag der Vinted-Abholliste: über die Kurznummer; passt keine (Vinted zeigt
   * dort evtl. die andere der beiden Nummern eines Pakets — gedruckter 17…-Code vs. VGS-Sticker), über den Namen des
   * Empfängers, sofern er genau ein offenes Vinted-Paket trifft. Nie raten, lieber „nicht gefunden".
   */
  const offeneVinted = () => (paketeRef.current || []).filter((p) => p && !p.deleted && !p.ausgegebenAm && !p.zurueckgeschickt && vintedCode(p));
  const gleich = (a, b) => String(a || '').trim().toLowerCase().replace(/\s+/g, ' ') === String(b || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const meins = (id, name) => {
    const perCode = offeneVinted().find((p) => vintedCode(p) === normCode(id));
    if (perCode || !name) return perCode || null;
    const perName = offeneVinted().filter((p) => gleich(p.name, name));
    return perName.length === 1 ? perName[0] : null;
  };

  const starte = async (code) => {
    const c = String(code || '').trim();
    if (!c || phase === 'sucht') return;
    setFehler(''); setPhase('sucht');
    const r = await pickupStart(c);
    if (!lebt.current) return;
    if (r.ok && Array.isArray(r.parcels)) { offen.current = true; setVinted(r.parcels); setPhase('liste'); return; }
    if (r.reason === 'rejected') { setFehler(`${T('abgelehnt')}${r.message ? ': ' + r.message : ''}`); setPin(''); setPhase('code'); return; }
    setFehler(r.message || r.reason || ''); setPhase('rueckfall');
  };

  const uebergeben = async () => {
    setPhase('laeuft');
    let alleOk = true;
    for (const v of vinted) {
      if (ergebnis[v.id] === 'ok') continue;
      const r = await pickupScan(v.id);
      if (!lebt.current) return;
      if (!r.ok) { alleOk = false; setErgebnis((e) => ({ ...e, [v.id]: 'fehler' })); setFehler(r.message || r.reason || ''); break; }
      setErgebnis((e) => ({ ...e, [v.id]: 'ok' }));
      if (r.done) offen.current = false;
      const p = meins(v.id, v.name);
      if (p) { await ausgebenRef.current(p); notiz('ausgabe_paketpilot', vintedScanCode(p), { ok: true }); }
    }
    if (!lebt.current) return;
    if (alleOk) { offen.current = false; setPhase('fertig'); } else { setPhase('rueckfall'); }
  };

  /** Ohne Vinted ausgeben: das angetippte Paket (bzw. die noch nicht bestätigten dieser Abholung). */
  const ohneVinted = async () => {
    const rest = vinted.length ? vinted.filter((v) => ergebnis[v.id] !== 'ok').map((v) => meins(v.id, v.name)).filter(Boolean) : [paket];
    if (offen.current) { offen.current = false; pickupCancel(); }
    for (const p of rest) {
      notiz('rueckfall', vintedScanCode(p), { reason: 'ausgabe', message: `Ausgabe ohne Vinted: ${fehler}`.slice(0, 200) });
      await ausgebenRef.current(p);
      notiz('ausgabe_paketpilot', vintedScanCode(p), { ok: true });
      await new Promise((r) => setTimeout(r, 150)); // Parent neu zeichnen lassen (aktueller Bestand)
    }
    onClose();
  };

  const schliessen = () => { if (offen.current) { offen.current = false; pickupCancel(); } onClose(); };

  const feld = { width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${f.rand}`, fontSize: 16, background: f.feld, color: f.text, boxSizing: 'border-box' };
  const knopf = (primary, farbe) => ({ width: '100%', padding: '12px', borderRadius: 10, marginTop: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', border: primary ? 'none' : `1px solid ${f.rand}`, background: primary ? (farbe || '#007782') : 'transparent', color: primary ? '#fff' : f.text });
  const gehoertDazu = !vinted.length || vinted.some((v) => normCode(v.id) === vintedCode(paket) || meins(v.id, v.name) === paket);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 7600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-testid="vinted-ausgabe" data-phase={phase} style={{ background: dark ? '#23272f' : '#fff', color: f.text, borderRadius: 14, width: '100%', maxWidth: 520, maxHeight: '92vh', overflowY: 'auto', padding: 18, boxShadow: '0 10px 40px rgba(0,0,0,0.4)' }}>
        <b style={{ fontSize: 18 }}>🧪 {T('ausgabeTitel')}</b>
        <div data-testid="va-live" style={{ marginTop: 8, padding: '6px 10px', borderRadius: 8, background: '#c62828', color: '#fff', fontSize: 13, fontWeight: 700 }}>🔴 {T('liveBadge')}</div>
        <div style={{ fontSize: 14, color: f.sub, marginTop: 4 }}>{paket.name}{paket.regalplatz ? ` · ${paket.regalplatz}` : ''}</div>

        {phase === 'code' && (
          <div>
            <div style={{ fontSize: 13, color: f.sub, marginTop: 10, lineHeight: 1.5 }}>{T('ausgabeHinweis')}</div>
            <div style={{ marginTop: 10 }}><ShelfQrScanner active eingebettet formats={['qr_code']} onCode={(text) => starte(text)} /></div>
            <form onSubmit={(e) => { e.preventDefault(); starte(pin); }} style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <input data-testid="va-pin" inputMode="numeric" placeholder={T('pin')} value={pin} onChange={(e) => setPin(e.target.value)} style={feld} />
              <button type="submit" data-testid="va-weiter" style={{ padding: '0 16px', borderRadius: 8, border: 'none', background: '#007782', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>{T('weiter')}</button>
            </form>
          </div>
        )}
        {phase === 'sucht' && <div style={{ marginTop: 16, fontSize: 15 }}>⏳ {T('sucht')}</div>}

        {(phase === 'liste' || phase === 'laeuft' || phase === 'fertig' || (phase === 'rueckfall' && vinted.length > 0)) && (
          <div data-testid="va-liste" style={{ marginTop: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{T('pakete')} ({vinted.length})</div>
            {vinted.map((v) => {
              const p = meins(v.id, v.name);
              return (
                <div key={v.id} data-ergebnis={ergebnis[v.id] || ''} style={{ padding: '8px 10px', borderRadius: 8, background: f.zeile, marginTop: 6, fontSize: 14 }}>
                  <b>{v.shown}</b> · {v.name}
                  <div style={{ fontSize: 13, color: f.sub, marginTop: 2 }}>
                    {p ? `📍 ${p.regalplatz || '—'}` : `⚠️ ${T('nichtInPP')}`}
                    {ergebnis[v.id] === 'ok' && <span style={{ color: '#2e7d32' }}> · ✅ {T('bestaetigt')}</span>}
                    {ergebnis[v.id] === 'fehler' && <span style={{ color: '#c62828' }}> · ✗ {T('bestaetigtNicht')}</span>}
                  </div>
                </div>
              );
            })}
            {!gehoertDazu && <div style={{ fontSize: 13, color: '#c62828', marginTop: 8 }}>⚠️ {paket.name}: {T('nichtBeiAbholer')}</div>}
          </div>
        )}

        {fehler && <div data-testid="va-fehler" style={{ marginTop: 10, fontSize: 14, color: '#c62828' }}>{fehler}</div>}
        {phase === 'rueckfall' && <div style={{ marginTop: 8, fontSize: 13, color: f.sub, lineHeight: 1.5 }}>{T('rueckfall')}</div>}

        {phase === 'liste' && <button type="button" data-testid="va-uebergeben" onClick={uebergeben} style={knopf(true)}>{T('uebergeben')}</button>}
        {phase === 'laeuft' && <div style={{ marginTop: 14, fontSize: 15 }}>⏳ {T('sucht')}</div>}
        {phase === 'rueckfall' && <button type="button" data-testid="va-ohne" onClick={ohneVinted} style={knopf(true, '#b26a00')}>{T('ohneVintedAusgeben')}</button>}
        {phase === 'fertig'
          ? <button type="button" data-testid="va-fertig" onClick={onClose} style={knopf(true)}>{T('fertig')}</button>
          : phase !== 'laeuft' && phase !== 'sucht' && <button type="button" data-testid="va-abbrechen" onClick={schliessen} style={knopf(false)}>{T('abbrechen')}</button>}
      </div>
    </div>
  );
}
